import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { useAtom } from 'jotai';
import { cn } from '../../../../utils/cn';
import { ReactComponent as SearchIcon } from '../../../../icons/svg/search.svg';
import { Button } from '../../../../components/button';
import { CategoriesMenu } from './CategoriesMenu';
import { CountrySelectMenu } from '../CountrySelectMenu';
import { UserCart } from './UserCart';
import type { HeaderCartInfo } from '../Header';
import {
    _headerVariants,
    type HeaderCountryElement,
    type HeaderNavLinks,
    type HeaderUserData,
} from '../Header';
import { WarningBanner } from '../../../warning-banner/components/WarningBanner';
import { DesktopUserMenu } from './DesktopUserMenu';
import { uiStateAtomSearchClicked } from '../../states/ui-states-search-clicked';
import { LogoAnimated } from '../../../../components/logo-animated/LogoAnimated';
import { RoccoLink } from '../../../../components/link/RoccoLink';

const _headerIconVariants = cva('', {
    variants: {
        fill: {
            dark: 'fill-gray-900',
            light: 'fill-white',
            none: 'fill-none',
        },
        outline: {
            default: '',
            light: 'stroke-white stroke-1',
            dark: 'stroke-gray-900 stroke-1',
        },
    },
    defaultVariants: {
        fill: 'dark',
        outline: 'default',
    },
});

interface DesktopHeaderProps extends VariantProps<typeof _headerVariants> {
    userData: HeaderUserData;
    primaryLinks: HeaderNavLinks;
    currentCountryCode: string;
    allowMemberGuides?: boolean;
    allowMemberOrders?: boolean;
    countries: HeaderCountryElement[];
    currentUrl: string;
    onInboxClick?: () => void;
    onLogout: () => void;
    onSearchClick?: () => void;
    onUserCartClick?: () => void;
    onLoginClick: () => void;
    additionalUserMenuItems?: HeaderNavLinks;
    warningMessages?: string[];
    isLegacyBrokenRemMode?: boolean;
    cartInfo: HeaderCartInfo;
}

export const DesktopHeader = (props: DesktopHeaderProps) => {
    const {
        color,
        userData,
        primaryLinks,
        currentCountryCode,
        allowMemberGuides,
        allowMemberOrders,
        countries,
        currentUrl,
        onInboxClick,
        onLogout,
        onUserCartClick,
        onLoginClick,
        additionalUserMenuItems,
        warningMessages,
        isLegacyBrokenRemMode,
        cartInfo,
    } = props;

    const mode =
        color === 'transparent' || color === 'none' ? 'float' : 'default';
    const menuColor = color === 'transparent' ? 'light' : 'dark';

    const outlineClass = _headerIconVariants({
        outline: menuColor,
        fill: 'none',
    });

    const [, setSearchClicked] = useAtom(uiStateAtomSearchClicked);

    const handleSearchClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        setSearchClicked(true);
        if (currentUrl.includes('/search')) {
            e.preventDefault();
        }
    };

    const { cartCount } = cartInfo;

    return (
        <div className={cn(_headerVariants({ color, mode }), 'rocco-header')}>
            <div className="flex h-24 items-center justify-between px-12 pb-6 pt-8 qhd:mx-auto qhd:max-w-[1920px] qhd:px-12">
                {/* Categories Menu */}
                <div className="flex-1">
                    <CategoriesMenu color={menuColor} links={primaryLinks} />
                </div>

                {/* Logo */}
                <div className="-ml-20 -mt-2 shrink-0">
                    <RoccoLink
                        to="/"
                        prefetch="intent"
                        aria-label="Home"
                        className={
                            color === 'transparent'
                                ? 'text-gray-50'
                                : 'text-gray-900'
                        }
                    >
                        <LogoAnimated
                            size={2.4}
                            isLegacyBrokenRemMode={isLegacyBrokenRemMode}
                            color={menuColor}
                        />
                    </RoccoLink>
                </div>

                <div className="flex flex-1 items-center justify-end gap-x-9">
                    <div className="flex gap-x-18">
                        <div className="flex items-center gap-x-8">
                            <RoccoLink
                                to="/search"
                                prefetch="intent"
                                onClick={handleSearchClick}
                            >
                                <SearchIcon
                                    className={cn(
                                        'relative size-6',
                                        outlineClass,
                                    )}
                                />
                            </RoccoLink>

                            <CountrySelectMenu
                                color={menuColor}
                                currentCountryCode={currentCountryCode}
                                countries={countries}
                            />
                        </div>
                    </div>

                    {userData.isAuthorized ? (
                        <div className="flex items-center gap-x-10">
                            <div className="flex items-center gap-x-7">
                                <UserCart
                                    fill={
                                        menuColor === 'dark' ? 'dark' : 'light'
                                    }
                                    onUserCartClick={() => {
                                        onUserCartClick?.();
                                    }}
                                    cartInfo={cartInfo}
                                />
                            </div>

                            <DesktopUserMenu
                                userData={userData}
                                color={menuColor}
                                onInboxClick={onInboxClick}
                                onLogout={onLogout}
                                additionalUserMenuItems={
                                    additionalUserMenuItems
                                }
                                allowMemberGuides={allowMemberGuides}
                                allowMemberOrders={allowMemberOrders}
                            />
                        </div>
                    ) : (
                        <div className="flex items-center gap-x-8">
                            {cartCount > 0 ? (
                                <div className="flex items-center gap-x-8">
                                    <UserCart
                                        fill={
                                            menuColor === 'dark'
                                                ? 'dark'
                                                : 'light'
                                        }
                                        onUserCartClick={() => {
                                            onUserCartClick?.();
                                        }}
                                        cartInfo={cartInfo}
                                    />
                                </div>
                            ) : null}
                            <RoccoLink
                                to={'#'}
                                onClick={(e) => {
                                    e.preventDefault();
                                    onLoginClick();
                                }}
                                aria-label="Log In"
                                className={cn(
                                    'text-base font-medium decoration-2 underline-offset-8 hover:underline',
                                    color === 'transparent'
                                        ? 'text-gray-50 hover:text-gray-50'
                                        : 'text-gray-900 hover:text-gray-900',
                                )}
                            >
                                Log In
                            </RoccoLink>
                            <div>
                                <Button
                                    color={
                                        color === 'transparent'
                                            ? 'glassy'
                                            : 'dark'
                                    }
                                    inverted={color === 'transparent'}
                                    weight="default"
                                    size={'larger'}
                                    onClick={onLoginClick}
                                >
                                    Sign Up
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            {!!warningMessages?.length && (
                <WarningBanner alerts={warningMessages} />
            )}
        </div>
    );
};
